import * as styles from "./CommercialConditionsTab.css";

import { useAtomValue, useSetAtom } from "jotai";
import { type JSX, useEffect } from "react";

import {
  Field,
  FieldContent,
  FieldError,
  FieldLabel,
} from "@frontend/components/atoms/Field";
import { Input } from "@frontend/components/atoms/Input";
import { NumberField } from "@frontend/components/atoms/NumberField";
import { Spinner } from "@frontend/components/atoms/Spinner";
import { Switch } from "@frontend/components/atoms/Switch";
import { useField } from "@frontend/hooks/form";
import { ReferenceField } from "@frontend/shared/reference-field";
import { Trans, useLingui } from "@lingui/react/macro";

import {
  conditionsAtom,
  conditionsForm,
  loadConditionsAtom,
} from "../stores/conditions";
import { PriceList } from "./PriceList";
import { Section } from "./Section";

const fields = conditionsForm.fields;

export type CommercialConditionsTabProps = {
  clientId: number;
};

export function CommercialConditionsTab({
  clientId,
}: CommercialConditionsTabProps): JSX.Element {
  const { t } = useLingui();

  const { options, loading, error, conflict } = useAtomValue(conditionsAtom);
  const load = useSetAtom(loadConditionsAtom);

  const paymentTypeId = useField(fields.paymentTypeId);
  const paymentEmail = useField(fields.paymentEmail);
  const paymentEmailCopyTo = useField(fields.paymentEmailCopyTo);
  const ongoing = useField(fields.ongoingAuthorized);
  const revision = useField(fields.priceRevisionActivation);

  useEffect(() => {
    void load(clientId);
  }, [load, clientId]);

  const needsEmail =
    true ===
    options.paymentTypes.find(({ id }) => id === paymentTypeId.value)
      ?.needsEmail;

  if (loading) {
    return (
      <div className={styles.loading}>
        <Spinner aria-label={t`Loading`} />
      </div>
    );
  }

  return (
    <div className={styles.tab}>
      {error ? (
        <p className={styles.alert} role="alert">
          {conflict ? (
            <Trans>
              Someone else saved this client while it was open. Reopen it to see
              their version, then make your change again.
            </Trans>
          ) : (
            error
          )}
        </p>
      ) : null}

      <div className={styles.columns}>
        <div className={styles.column}>
          <Section icon="CreditCard" legend={t`Payment`}>
            <ReferenceField
              label={t`Type`}
              placeholder={t`Please choose an option`}
              items={options.paymentTypes.map((paymentType) => ({
                id: paymentType.id,
                label: paymentType.label,
                group: null,
                available: null,
              }))}
              field={fields.paymentTypeId}
            />

            {needsEmail ? (
              <>
                <Field>
                  <FieldLabel>{t`Billing address`}</FieldLabel>

                  <FieldContent>
                    <Input
                      value={paymentEmail.value ?? ""}
                      invalid={Boolean(paymentEmail.error)}
                      onValueChange={(next) => paymentEmail.setValue(next)}
                    />

                    <FieldError match={Boolean(paymentEmail.error)}>
                      {paymentEmail.error}
                    </FieldError>
                  </FieldContent>
                </Field>

                <Field>
                  <FieldLabel>{t`Copy to`}</FieldLabel>

                  <FieldContent>
                    <Input
                      value={paymentEmailCopyTo.value ?? ""}
                      onValueChange={(next) =>
                        paymentEmailCopyTo.setValue(next)
                      }
                    />
                  </FieldContent>
                </Field>
              </>
            ) : null}

            <ReferenceField
              label={t`Payment target`}
              placeholder={t`Please choose an option`}
              items={options.paymentTargets}
              field={fields.paymentTargetId}
            />
          </Section>

          <Section icon="Repeat" legend={t`Subscriptions`}>
            <ReferenceField
              label={t`Period`}
              placeholder={t`Please choose an option`}
              items={options.paymentPeriods}
              field={fields.subscriptionPaymentPeriodId}
            />

            <ReferenceField
              label={t`Billing time`}
              placeholder={t`Please choose an option`}
              items={options.billingTimes}
              field={fields.subscriptionBillingTimeId}
            />
          </Section>

          <Section icon="Zap" legend={t`Interventions`}>
            <ReferenceField
              label={t`Period`}
              placeholder={t`Please choose an option`}
              items={options.paymentPeriods}
              field={fields.interventionPaymentPeriodId}
            />
          </Section>
        </div>

        <div className={styles.column}>
          <Section icon="ShieldAlert" legend={t`Credit`}>
            <Field>
              <FieldLabel>{t`Ongoing authorized`}</FieldLabel>

              <FieldContent>
                <NumberField
                  value={ongoing.value ?? null}
                  min={0}
                  onValueChange={(next) => ongoing.setValue(next)}
                />
              </FieldContent>
            </Field>

            <ReferenceField
              label={t`Risk class`}
              placeholder={t`Please choose an option`}
              items={options.riskClasses}
              field={fields.riskClassId}
            />

            <Field>
              <FieldLabel>{t`Yearly revision`}</FieldLabel>

              <FieldContent>
                <Switch
                  checked={revision.value ?? true}
                  onCheckedChange={(next) => revision.setValue(next)}
                />
              </FieldContent>
            </Field>
          </Section>

          <PriceList />
        </div>
      </div>
    </div>
  );
}
